import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * ChatContext — persists Chat page UI state across navigation AND F5 refresh.
 * Lives above the router so it never unmounts when switching pages.
 *
 * Uses "Direct Consume" pattern (same as LiveTracking/Geofencing/Dashboard).
 * See /app/memory/REFACTOR_NOTES.md for pattern reference.
 *
 * Persists:
 *   - selectedConversationId: the currently open conversation (by ID, not full object,
 *     so the consumer can re-resolve from fresh data on mount and stale references
 *     don't accumulate).
 *   - drafts: { [conversationId]: draftText } — per-conversation drafts. Matches the
 *     behaviour of WhatsApp / Telegram / Slack / Discord / Teams. Each conversation
 *     remembers its own draft independently. Switching conversations swaps the
 *     visible draft to the one belonging to the newly-selected conversation. Also
 *     survives F5 refresh as soft data-loss protection.
 *   - searchQuery: the conversation-list search filter.
 *
 * Intentionally NOT persisted (resets on nav):
 *   - messages, conversations, tenantUsers, readReceipts → server data
 *   - all dialog/modal flags (isEmojiPickerOpen, isNewChatOpen, etc.)
 *   - bulk-selection mode (selectedConversations, isSelectionMode)
 *   - transient mention popup state
 *   - typing indicators
 */
const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

const STORAGE_KEY = 'ch_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

// One-time migration: old single-global `messageInput` → per-conversation `drafts` map.
// If the user had a draft typed under the previous implementation, attach it to the
// last-selected conversation so it doesn't get silently lost.
const buildInitialDrafts = (saved) => {
  const drafts = saved?.drafts && typeof saved.drafts === 'object' ? { ...saved.drafts } : {};
  if (saved?.messageInput && saved?.selectedConversationId && !drafts[saved.selectedConversationId]) {
    drafts[saved.selectedConversationId] = saved.messageInput;
  }
  return drafts;
};

export const ChatProvider = ({ children }) => {
  const saved = loadPersistedState();

  const [selectedConversationId, setSelectedConversationId] = useState(saved?.selectedConversationId || null);
  const [drafts, setDrafts] = useState(() => buildInitialDrafts(saved));
  const [searchQuery, setSearchQuery] = useState(saved?.searchQuery || '');

  useEffect(() => {
    const state = { selectedConversationId, drafts, searchQuery };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [selectedConversationId, drafts, searchQuery]);

  // Read a single conversation's draft (empty string if none).
  const getDraft = useCallback((conversationId) => {
    if (!conversationId) return '';
    return drafts[conversationId] || '';
  }, [drafts]);

  // Write a single conversation's draft. Empty strings remove the key entirely
  // to keep the persisted map small.
  const setDraft = useCallback((conversationId, text) => {
    if (!conversationId) return;
    setDrafts((prev) => {
      if (!text) {
        if (!(conversationId in prev)) return prev;
        const next = { ...prev };
        delete next[conversationId];
        return next;
      }
      if (prev[conversationId] === text) return prev;
      return { ...prev, [conversationId]: text };
    });
  }, []);

  return (
    <ChatContext.Provider value={{
      selectedConversationId, setSelectedConversationId,
      drafts, setDrafts,
      getDraft, setDraft,
      searchQuery, setSearchQuery,
    }}>
      {children}
    </ChatContext.Provider>
  );
};
